import type { NativeWorkspaceEnvironment } from '@/lib/native-desktop';

export type EnvironmentBuildResult =
  | { ok: true; environment: NativeWorkspaceEnvironment }
  | { ok: false; error: string };

/**
 * Parse the `NAME=value` env textarea and validate the draft's actions,
 * returning either the assembled environment or the first validation error.
 * Pure: all UI state stays with the caller.
 */
export function buildWorkspaceEnvironment(
  draft: NativeWorkspaceEnvironment,
  envText: string,
): EnvironmentBuildResult {
  const env: Record<string, string> = {};
  for (const [index, line] of envText.split('\n').entries()) {
    const trimmed = line.trim();
    if (trimmed.length === 0) continue;
    const separator = trimmed.indexOf('=');
    if (separator <= 0) {
      return { ok: false, error: `Invalid variable on line ${index + 1}; use NAME=value.` };
    }
    env[trimmed.slice(0, separator).trim()] = trimmed.slice(separator + 1);
  }
  for (const [index, action] of (draft.actions ?? []).entries()) {
    if (action.name.trim().length === 0) {
      return { ok: false, error: `Action ${index + 1} needs a name.` };
    }
    if (
      action.script.trim().length === 0 &&
      Object.values(action.scripts ?? {}).every((script) => script.trim().length === 0)
    ) {
      return { ok: false, error: `Action “${action.name}” needs at least one script.` };
    }
  }
  return { ok: true, environment: { ...draft, env } };
}
